<template>
  <PVProgressSpinner
    v-bind="$attrs"
    :data-testid="dataTestid"
    :stroke-width="strokeWidth"
    :class="spinnerClasses"
    :aria-label="label"
    role="status"
  />
</template>

<script lang="ts">
/**
 * PrimeVue `ProgressSpinner` with standard sizing.
 *
 * Why we override `--p-progressspinner-color-*`:
 * The generated preset still ships placeholder `progressspinner` tokens (red/blue/green/yellow).
 * Prime paints the first animation frame from `color-one`, so the spinner looks “wrong” (e.g. red)
 * until Product defines real component tokens. Until then we use `--p-primary-color`.
 *
 * We only style the primary case: standalone `<b-spinner>` usage in the app does not set `variant`
 * (Bootstrap-Vue default). `b-button` `spinner-variant="info"` is separate and not migrated here.
 */
export default {
  name: 'StandardSpinner',
  compatConfig: { MODE: 3 },
};
</script>

<script setup lang="ts">
import { computed } from 'vue';

const props = withDefaults(
  defineProps<{
    size?: 'sm' | 'md';
    label?: string;
    dataTestid?: string;
  }>(),
  {
    size: 'md',
    label: 'Loading...',
  },
);

const strokeWidth = computed((): string => (props.size === 'sm' ? '3' : '2'));

const spinnerClasses = computed(
  (): Record<string, boolean> => ({
    'standard-spinner': true,
    'standard-spinner--sm': props.size === 'sm',
  }),
);
</script>

<style scoped lang="scss">
.standard-spinner {
  --p-progressspinner-color-one: var(--p-primary-color);
  --p-progressspinner-color-two: var(--p-primary-color);
  --p-progressspinner-color-three: var(--p-primary-color);
  --p-progressspinner-color-four: var(--p-primary-color);

  display: inline-flex;
  flex-shrink: 0;
  line-height: 1;
  vertical-align: middle;

  &.p-progressspinner {
    width: 2rem;
    height: 2rem;
  }

  &--sm.p-progressspinner {
    width: 1rem;
    height: 1rem;
  }
}
</style>
